<script setup>
import {computed, inject, onMounted, provide, ref, watch} from "vue";
import {useStore} from "vue2-helpers/vuex";
import {accountTypePremium} from "@/constants/accountTypes";
import {filterTypesByCreator as defaultFilterTypesByCreator} from "@/constants/trackers";
import trackersService from "@/services/trackers.service";
import TrackerItem from "@/views/trackers/trackers/trackers/TrackerItem.vue";
import Loader from "@/components/ui/Loader.vue";
import ScrolledBottom from "@/components/ScrolledBottom.vue";
import constants from "@/constants/constants";
import TrackerProjectEdit from "@/components/trackers/TrackerProjectEdit.vue";

const props = defineProps(['hideFilter', 'folderId', 'folderType', 'userCanAddTracker']);
const {hideFilter = false, folderId = 0, folderType = null} = props;
const loading = ref(false);

const defaultFilterTypesByType = {
    'all': 'All Trackers',
    'private': 'Private Trackers',
    'public': 'Public Trackers',
    'ecosystems': 'Ecosystem Trackers',
    'POC': 'POC Trackers',
    'project': 'Projects',
}

const sortingTypes = {
    'default': 'Default order (Favorite first)',
    'created_at-desc': 'Date created (Newest first)',
    'created_at-asc': 'Date created (Oldest first)',
    'updated_at-desc': 'Date modified (Latest first)',
    'updated_at-asc': 'Date modified (Oldest first)',
    'name-asc': 'Alphabetically (A-Z)',
    'name-desc': 'Alphabetically (Z-A)',
    'startups_count-asc': 'Number of startups (Low to high)',
    'startups_count-desc': 'Number of startups (High to low)',
}

const store = useStore();

const filterByCreator = inject('filterByCreator');
const search = inject('search');
const updateSearch = inject('updateSearch');

const counts = ref(null);
const totalCounts = ref(null);
const status = ref('active');
const filterByType = ref('all');
const sorting = ref('default');
const trackers = ref([]);
const page = ref(1);
const hasMore = ref(false);
const addTrackerModal = ref(null);

const hasTrackers = computed(() => totalCounts.value?.active > 0 || totalCounts.value?.archived > 0);
const sortText = computed(() => sorting.value === 'default' ? 'Sort Trackers' : sortingTypes[sorting.value]);

const filterTypesByType = computed(() => {
    let types = Object.entries(defaultFilterTypesByType);

    if (!store.getters.settings.enable_projects) {
        types = types.filter(([value]) => value !== 'project');
    }

    if (store.getters.isCoPaceEnterprise) {
        types.splice(1, 0, ['request', 'Requests']);
    }

    return Object.fromEntries(types);
});

const filterText = computed(() => {
    let text = '';

    if (filterByCreator.value) {
        if (filterByCreator.value !== 'all') {
            if (filterByType.value !== 'all') {
                text += filterTypesByType.value[filterByType.value];
            } else {
                text += 'Trackers ';
            }

            text += ' ' + filterTypesByCreator.value[filterByCreator.value ?? 'all'];
        } else {
            text += filterTypesByType.value[filterByType.value];
        }
    }

    return text;
});

const filterTypesByCreator = computed(() => {
    const types = {...defaultFilterTypesByCreator};

    if (store.state.user.company_id === constants.companyCoPace) {
        types['approval-pending'] = 'Approval Pending';
    }

    return types;
});

onMounted(() => {
    getTotalCounts();
});

watch([filterByCreator, filterByType, sorting, status], () => {
    loadList();
});

watch(updateSearch, (value) => {
    if (value) {
        loadList();
    }
});

watch(() => store.state.needUpdateTrackers, (value) => {
    if (value) {
        getTotalCounts();
    }
});

async function getTotalCounts() {
    const {data} = await trackersService.getNumber(folderId);
    totalCounts.value = data;

    if (hasTrackers.value) {
        if (status.value === 'active' && totalCounts.value.active === 0 && totalCounts.value.archived > 0) {
            status.value = 'archived';
        }

        if (status.value === 'archived' && totalCounts.value.archived === 0 && totalCounts.value.active > 0) {
            status.value = 'active';
        }

        loadList();
    }
}

async function getTrackers() {
    return trackersService.getTrackersIndex(folderId, status.value, filterByCreator.value, filterByType.value, search.value, sorting.value, page.value);
}

async function loadList() {
    if (hasTrackers.value && filterByCreator.value) {
        loading.value = true;
        page.value = 1;

        const {data} = await getTrackers();
        trackers.value = data.trackers;
        hasMore.value = data.has_more;
        counts.value = data.counts;
        loading.value = false;
    }
}

async function loadMore() {
    if (hasMore.value) {
        hasMore.value = false;

        page.value++;
        const {data} = await getTrackers();

        trackers.value = trackers.value.concat(data.trackers);
        hasMore.value = data.has_more;
    }
}

async function reloadPermissions(trackerId) {
    const {data} = await trackersService.getPermissions(trackerId);
    trackers.value.find(tracker => tracker.id === trackerId).permissions = data;
}

async function reloadAccessSettings(trackerId) {
    const {data} = await trackersService.getAccessSettings(trackerId);
    trackers.value.find(tracker => tracker.id === trackerId).access_type = data[0];
    trackers.value.find(tracker => tracker.id === trackerId).ecosystems = data[1];
}

provide('updateTrackers', getTotalCounts)

defineExpose({totalCounts});
</script>

<template>
    <div v-if="totalCounts !== null">
        <template v-if="hasTrackers">
            <header class="main-inner__header">
                <h3 v-if="store.state.accountType === accountTypePremium && !store.getters.isEcosystem && !hideFilter" class="heading3-dropdown mr-auto">
                    <b-dropdown class="" :text="filterText" boundary="viewport">
                        <template v-for="(name, value) in filterTypesByType" >
                            <b-dropdown-item
                                v-if="!(value === 'ecosystems' && store.getters.settings.disable_ecosystems_trackers)"
                                :key="value"
                                @click="filterByType = value"
                            >
                                {{ name }}
                            </b-dropdown-item>
                        </template>
                    </b-dropdown>
                </h3>

                <template v-if="counts !== null">
                    <div class="main-inner__header">
                        <div v-if="status === 'active'">
                            <a v-if="counts.archived > 0" @click="status = 'archived'" class="link-archive">Show archived</a>
                        </div>

                        <div v-if="status === 'archived'">
                            <a v-if="counts.active > 0" @click="status = 'active'" class="link-archive">Show active</a>
                        </div>

                        <a
                            v-if="userCanAddTracker"
                            class="link link--add"
                            @click="addTrackerModal.open()"
                        >
                            New Tracker
                        </a>

                        <b-dropdown class="dropdown--sort" :text="sortText" boundary="viewport">
                            <b-dropdown-item
                                v-for="(name, value) in sortingTypes"
                                :key="value"
                                :class="{active: sorting === value}"
                                @click="sorting = value"
                            >
                                {{ name }}
                            </b-dropdown-item>
                        </b-dropdown>
                    </div>
                </template>
            </header>

            <Loader
                v-if="loading"
            />

            <div v-else class="card--trackers-wrapper">
                <TrackerItem
                    v-for="tracker in trackers"
                    :key="tracker.id"
                    :tracker="tracker"
                    :folder-id="folderId"
                    @edit="addTrackerModal.open(tracker.id, tracker.type)"
                    @permissionsUpdated="reloadPermissions(tracker.id)"
                    @accessSettingsUpdated="reloadAccessSettings(tracker.id)"
                />

                <ScrolledBottom @scrolledBottom = loadMore />
            </div>
        </template>

        <div v-else class="empty-tracker">
            <a
                v-if="userCanAddTracker"
                class="link"
                @click="addTrackerModal.open()"
            >
                + New Tracker
            </a>

            <div class="empty-tracker__inner">
                <p>
                    Trackers allow you to keep tabs on startups and perform diligence on them. You can also use trackers to create steps  to move startups through as you gather more information to determine their compatibility with your needs.
                </p>

                <div v-if="userCanAddTracker" class="card__controls ml-auto">
                    <b-button variant="primary" size="lg" @click="addTrackerModal.open()">Create A Tracker</b-button>
                </div>
            </div>
        </div>

        <TrackerProjectEdit
            ref="addTrackerModal"
            :folder-id="folderId"
            :folder-type="folderType"
            @trackerSaved="getTotalCounts"
        />
    </div>
</template>

<style scoped>

</style>
